import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Tag, X } from "lucide-react";
import { Button } from "@satyrsmc/shared/components";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Pager, usePageParam } from "@/components/ui/pager";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/trpc";
import { GalleryLightbox } from "@/components/members-section/GalleryLightbox";
import { LightboxPanelSection, PhotoFacts } from "@/components/members-section/LightboxPanel";
import { MediaStatus } from "@satyrsmc/shared/lib/enums";
import type { AdminMediaGalleryOption, AdminMediaItem } from "@satyrsmc/shared/dto/admin/media";

/**
 * An admin media item reshaped for the shared fullscreen lightbox. No
 * `uploader` on purpose: the admin panel extra renders its own uploader link
 * (into the admin contact page), so the built-in "Submitted by" stays out.
 */
function toLightboxPhoto(item: AdminMediaItem) {
  return {
    id: item.id,
    thumbnail_url: item.thumbnail_url ?? item.preview_url ?? "",
    web_url: item.preview_url ?? item.thumbnail_url ?? "",
    width: item.width,
    height: item.height,
    description: item.description,
    created_at: item.created_at,
    tags: item.tags,
  };
}

/** Filter value standing in for "no event filter" — Select has no empty option. */
const ALL_EVENTS = "all";

/**
 * The uploader's name, linking through to their admin contact page. These
 * links render inside the always-dark lightbox panel, so the colors are fixed
 * rather than theme-driven.
 */
function UploaderLink({ item }: { item: AdminMediaItem }) {
  return (
    <Link
      to={`/admin/contacts/${item.uploader_contact_id}`}
      className="text-yellow-400 underline-offset-2 hover:underline"
    >
      {item.uploader_name}
    </Link>
  );
}

/** The photo's event (or gallery title), linking through to the member gallery. */
function GalleryLink({ item }: { item: AdminMediaItem }) {
  return (
    <Link
      to={`/gallery/${item.gallery_id}`}
      className="text-yellow-400 underline-offset-2 hover:underline"
    >
      {item.event_name ?? item.gallery_title}
    </Link>
  );
}

/**
 * Inline per-photo description editor. Descriptions are written by uploaders
 * as captions and curated here; they surface in the member lightbox.
 */
function MediaDescriptionEditor({ item }: { item: AdminMediaItem }) {
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const mutation = trpc.admin.media.updateDescription.useMutation({
    onSuccess: () => {
      toast.success("Description saved.");
      utils.admin.media.list.invalidate();
      setEditing(false);
    },
    onError: (err) => toast.error(err.message),
  });

  if (!editing) {
    return (
      <div className="flex items-start justify-between gap-2">
        {item.description ? (
          <p className="min-w-0 flex-1 whitespace-pre-wrap text-sm">{item.description}</p>
        ) : (
          <p className="min-w-0 flex-1 text-sm italic text-zinc-400">No description</p>
        )}
        <button
          type="button"
          aria-label={`Edit description of ${item.filename}`}
          className="shrink-0 text-xs text-yellow-400 hover:underline"
          onClick={() => {
            setDraft(item.description ?? "");
            setEditing(true);
          }}
        >
          Edit
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <Textarea
        aria-label={`Description of ${item.filename}`}
        value={draft}
        maxLength={2000}
        rows={2}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate({ media_id: item.id, description: draft.trim() || null })}
        >
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * The admin curation panel rendered into the lightbox sidebar — file
 * metadata, uploader and gallery links, the description editor, and (while
 * pending) the approve/deny actions. Tag review lives in the lightbox's
 * built-in Tagged section, not here. The list view keeps only the image and
 * an X.
 */
function AdminMediaPanel({
  item,
  pending,
  gallery,
  busy = false,
  saving = false,
  onApprove,
  onDeny,
}: {
  item: AdminMediaItem;
  pending: boolean;
  /** The photo's event gallery, when it has one — enables the cover control. */
  gallery?: AdminMediaGalleryOption;
  busy?: boolean;
  saving?: boolean;
  onApprove?: () => void;
  onDeny?: () => void;
}) {
  return (
    <div className="space-y-4 border-t border-zinc-700 pt-4">
      <LightboxPanelSection label="File">
        <p className="truncate font-medium" title={item.filename}>
          {item.filename}
        </p>
        {item.original_filename && (
          <p className="truncate text-xs text-zinc-400" title={item.original_filename}>
            was {item.original_filename}
          </p>
        )}
        <PhotoFacts
          width={item.width}
          height={item.height}
          byteSize={item.byte_size}
          uploadedAt={item.created_at}
          className="text-xs text-zinc-400"
        />
      </LightboxPanelSection>
      <LightboxPanelSection label="Submitted by">
        <UploaderLink item={item} />
      </LightboxPanelSection>
      <LightboxPanelSection label="Gallery">
        <GalleryLink item={item} />
      </LightboxPanelSection>
      <LightboxPanelSection label="Description">
        <MediaDescriptionEditor item={item} />
      </LightboxPanelSection>
      {!pending && gallery && <CoverControl item={item} gallery={gallery} />}
      {pending && (
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={busy}
            aria-label={`Approve ${item.filename}`}
            onClick={onApprove}
          >
            {saving ? "Saving…" : "Approve"}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={busy}
            aria-label={`Deny ${item.filename}`}
            onClick={onDeny}
          >
            Deny
          </Button>
        </div>
      )}
    </div>
  );
}

/** Sidebar control marking an approved photo as its gallery's cover. */
function CoverControl({
  item,
  gallery,
}: {
  item: AdminMediaItem;
  gallery: AdminMediaGalleryOption;
}) {
  const setCover = useSetGalleryCover();

  return (
    <LightboxPanelSection label="Gallery cover">
      {gallery.cover_media_id === item.id ? (
        <p className="text-zinc-300">
          {gallery.cover_is_custom ? "This photo is the cover." : "The cover, as the newest photo."}
        </p>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          className="mt-1"
          aria-label={`Set ${item.filename} as cover`}
          disabled={setCover.isPending}
          onClick={() => setCover.mutate({ gallery_id: gallery.id, media_id: item.id })}
        >
          Set as cover
        </Button>
      )}
    </LightboxPanelSection>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  );
}

/**
 * One pending photo in the review grid: just the image, a bulk-select
 * checkbox, an X to delete (deny), and a tag marker when the photo carries
 * contact tags. Everything else lives in the lightbox sidebar — click the
 * photo to open it.
 */
function PendingCard({
  item,
  selected,
  onSelect,
  onDelete,
  onView,
  busy,
  saving,
}: {
  item: AdminMediaItem;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  onDelete: () => void;
  onView: () => void;
  busy: boolean;
  saving: boolean;
}) {
  return (
    <div className="relative aspect-square overflow-hidden rounded-lg border bg-muted">
      {item.preview_url && (
        <button
          type="button"
          aria-label={`View ${item.filename}`}
          className="block size-full"
          onClick={onView}
        >
          <img
            src={item.preview_url}
            alt={item.filename}
            className="size-full object-cover"
            loading="lazy"
          />
        </button>
      )}
      {saving && (
        <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 bg-black/50 text-sm font-medium text-white">
          <Loader2 className="size-4 animate-spin" />
          Saving…
        </div>
      )}
      <input
        type="checkbox"
        aria-label={`Select ${item.filename}`}
        checked={selected}
        onChange={(e) => onSelect(e.target.checked)}
        className="absolute left-2 top-2 size-4 cursor-pointer accent-satyrs-blue"
      />
      <button
        type="button"
        aria-label={`Delete ${item.filename}`}
        disabled={busy}
        onClick={onDelete}
        className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80 disabled:opacity-50"
      >
        <X className="size-4" />
      </button>
      {item.tags.length > 0 && (
        <div
          aria-hidden="true"
          title="Has tags"
          className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-black/60 px-1.5 py-1 text-xs font-medium text-white"
        >
          <Tag className="size-3.5" />
          {item.tags.length}
        </div>
      )}
    </div>
  );
}

function PendingQueue() {
  const utils = trpc.useUtils();
  const { confirm, confirmDialog } = useConfirm();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [page, setPage] = usePageParam();

  const { data, isLoading } = trpc.admin.media.list.useQuery({ status: MediaStatus.Pending, page });
  const items = data?.items ?? [];

  function refresh() {
    utils.admin.media.list.invalidate();
    utils.admin.media.pendingCount.invalidate();
    setSelectedIds([]);
  }

  const approveMutation = trpc.admin.media.approve.useMutation({
    onSuccess: refresh,
    onError: (err) => toast.error(err.message),
  });
  const denyMutation = trpc.admin.media.deny.useMutation({
    onSuccess: refresh,
    onError: (err) => toast.error(err.message),
  });
  const bulkDenyMutation = trpc.admin.media.bulkDeny.useMutation({
    onSuccess: (result) => {
      refresh();
      if (result.failed.length > 0) {
        toast.error(
          `${result.denied.length} deleted, ${result.failed.length} failed: ${result.failed
            .map((f) => f.error)
            .join("; ")}`,
        );
      } else {
        toast.success(`${result.denied.length} deleted.`);
      }
    },
    onError: (err) => toast.error(err.message),
  });

  async function handleBulkDeny() {
    const ok = await confirm(
      `The files for ${selectedIds.length} ${selectedIds.length === 1 ? "photo" : "photos"} will be permanently deleted. This cannot be undone.`,
      "Delete the selected photos?",
    );
    if (!ok) return;
    bulkDenyMutation.mutate({ ids: selectedIds });
  }

  const bulkApproveMutation = trpc.admin.media.bulkApprove.useMutation({
    onSuccess: (result) => {
      refresh();
      if (result.failed.length > 0) {
        toast.error(
          `${result.approved.length} approved, ${result.failed.length} failed: ${result.failed
            .map((f) => f.error)
            .join("; ")}`,
        );
      } else {
        toast.success(`${result.approved.length} approved.`);
      }
    },
    onError: (err) => toast.error(err.message),
  });

  // The photos being approved right now — the thumbnail cut takes a moment,
  // and the reviewer deserves to see which cards are mid-save.
  const savingIds =
    approveMutation.isPending && approveMutation.variables
      ? [approveMutation.variables.id]
      : bulkApproveMutation.isPending && bulkApproveMutation.variables
        ? bulkApproveMutation.variables.ids
        : [];

  const busy =
    approveMutation.isPending ||
    denyMutation.isPending ||
    bulkApproveMutation.isPending ||
    bulkDenyMutation.isPending;

  async function handleDeny(item: AdminMediaItem) {
    const ok = await confirm(
      `The original and web copies of "${item.filename}" will be permanently deleted. This cannot be undone.`,
      "Deny this photo?",
    );
    if (!ok) return;
    denyMutation.mutate({ id: item.id });
  }

  function toggleSelectAll(checked: boolean) {
    setSelectedIds(checked ? items.map((item) => item.id) : []);
  }

  function toggleOne(id: string, checked: boolean) {
    setSelectedIds((ids) => (checked ? [...ids, id] : ids.filter((existing) => existing !== id)));
  }

  if (isLoading) return <Spinner />;

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing is waiting for review.</p>;
  }

  const allSelected = selectedIds.length === items.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            aria-label="Select all pending photos"
            checked={allSelected}
            onChange={(e) => toggleSelectAll(e.target.checked)}
            className="size-4 cursor-pointer accent-satyrs-blue"
          />
          Select all
        </label>
        <Button
          size="sm"
          disabled={busy || selectedIds.length === 0}
          onClick={() => bulkApproveMutation.mutate({ ids: selectedIds })}
        >
          Approve selected ({selectedIds.length})
        </Button>
        <Button
          size="sm"
          variant="destructive"
          disabled={busy || selectedIds.length === 0}
          onClick={() => void handleBulkDeny()}
        >
          Delete selected ({selectedIds.length})
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {items.map((item) => (
          <PendingCard
            key={item.id}
            item={item}
            selected={selectedIds.includes(item.id)}
            onSelect={(checked) => toggleOne(item.id, checked)}
            onDelete={() => void handleDeny(item)}
            onView={() => setLightboxIndex(items.indexOf(item))}
            saving={savingIds.includes(item.id)}
            busy={busy}
          />
        ))}
      </div>

      <Pager
        page={data?.page ?? page}
        perPage={data?.per_page ?? 24}
        total={data?.total ?? 0}
        onPageChange={(next) => {
          setPage(next);
          setSelectedIds([]);
        }}
      />

      <GalleryLightbox
        photos={items.map(toLightboxPhoto)}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        canEditTags
        renderPanelExtra={(photoId) => {
          const item = items.find((candidate) => candidate.id === photoId);
          if (!item) return null;
          return (
            <AdminMediaPanel
              item={item}
              pending
              busy={busy}
              saving={savingIds.includes(item.id)}
              onApprove={() => approveMutation.mutate({ id: item.id })}
              onDeny={() => void handleDeny(item)}
            />
          );
        }}
      />
      {confirmDialog}
    </div>
  );
}

/**
 * Choose a gallery's cover, or pass a null media id to go back to the newest
 * approved photo. The cover shows on the members' gallery index and
 * dashboard card, so both member reads are refreshed alongside the options.
 */
function useSetGalleryCover() {
  const utils = trpc.useUtils();
  return trpc.admin.media.setGalleryCover.useMutation({
    onSuccess: (_gallery, input) => {
      void utils.admin.media.galleryOptions.invalidate();
      void utils.members.gallery.list.invalidate();
      toast.success(
        input.media_id === null ? "Cover reset to the newest photo." : "Cover updated.",
      );
    },
    onError: (err) => toast.error(err.message),
  });
}

/** The gallery's current cover thumbnail, and a way back to the automatic pick. */
function GalleryCover({ gallery }: { gallery: AdminMediaGalleryOption }) {
  const setCover = useSetGalleryCover();

  return (
    <div className="flex items-center gap-3">
      {gallery.cover_url ? (
        <img
          src={gallery.cover_url}
          alt="Gallery cover"
          className="size-16 shrink-0 rounded-md object-cover"
        />
      ) : (
        <div className="size-16 shrink-0 rounded-md bg-muted" />
      )}
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-medium">Cover photo</p>
        <p className="text-muted-foreground">
          {gallery.cover_is_custom
            ? "Chosen by an admin."
            : gallery.cover_url
              ? "The newest photo, until you choose one from a photo's details."
              : "Appears once a photo is approved."}
        </p>
      </div>
      {gallery.cover_is_custom && (
        <Button
          size="sm"
          variant="outline"
          disabled={setCover.isPending}
          onClick={() => setCover.mutate({ gallery_id: gallery.id, media_id: null })}
        >
          Use newest photo
        </Button>
      )}
    </div>
  );
}

/**
 * Title, cover, and description of the gallery selected in the filter, with
 * an inline editor so admins can write the blurb without leaving the media page.
 */
function GalleryHeader({ gallery }: { gallery: AdminMediaGalleryOption }) {
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const updateMutation = trpc.admin.media.updateGallery.useMutation({
    onSuccess: () => {
      utils.admin.media.galleryOptions.invalidate();
      setEditing(false);
      toast.success("Description saved.");
    },
    onError: (err) => toast.error(err.message),
  });

  function startEditing() {
    setDraft(gallery.description ?? "");
    setEditing(true);
  }

  function save() {
    const trimmed = draft.trim();
    updateMutation.mutate({ gallery_id: gallery.id, description: trimmed === "" ? null : trimmed });
  }

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <h2 className="text-lg font-semibold">{gallery.title}</h2>
      <GalleryCover gallery={gallery} />
      {editing ? (
        <div className="space-y-2">
          <Textarea
            aria-label="Gallery description"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder="Describe this gallery…"
          />
          <div className="flex gap-2">
            <Button size="sm" disabled={updateMutation.isPending} onClick={save}>
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={updateMutation.isPending}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3">
          {gallery.description ? (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {gallery.description}
            </p>
          ) : (
            <p className="text-sm italic text-muted-foreground">No description yet.</p>
          )}
          <Button size="sm" variant="outline" onClick={startEditing}>
            Edit description
          </Button>
        </div>
      )}
    </div>
  );
}

function ApprovedBrowser() {
  const [eventId, setEventId] = useState(ALL_EVENTS);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [page, setPage] = usePageParam();
  const utils = trpc.useUtils();
  const { confirm, confirmDialog } = useConfirm();
  const { data: galleryOptions } = trpc.admin.media.galleryOptions.useQuery();
  const { data, isLoading } = trpc.admin.media.list.useQuery({
    status: MediaStatus.Approved,
    event_id: eventId === ALL_EVENTS ? undefined : eventId,
    page,
  });

  const removeMutation = trpc.admin.media.removeApproved.useMutation({
    onSuccess: () => {
      toast.success("Photo deleted.");
      utils.admin.media.list.invalidate();
      utils.admin.media.galleryOptions.invalidate();
      utils.admin.media.pendingCount.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  async function handleRemove(item: AdminMediaItem) {
    const ok = await confirm(
      `"${item.filename}" and its files will be permanently deleted and disappear from the member gallery. This cannot be undone.`,
      "Delete this photo?",
    );
    if (!ok) return;
    removeMutation.mutate({ id: item.id });
  }

  const items = data?.items ?? [];
  const selectedGallery =
    eventId === ALL_EVENTS
      ? undefined
      : (galleryOptions?.items ?? []).find((option) => option.event_id === eventId);
  // Cover controls need the photo's gallery option; event-less galleries have
  // none, so their photos simply offer no cover control.
  const galleriesById = new Map((galleryOptions?.items ?? []).map((option) => [option.id, option]));

  return (
    <div className="space-y-4">
      <div className="w-full max-w-xs">
        <Select
          value={eventId}
          onValueChange={(next) => {
            setEventId(next);
            setPage(1);
          }}
        >
          <SelectTrigger aria-label="Event">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_EVENTS}>All events</SelectItem>
            {(galleryOptions?.items ?? []).map((option) => (
              <SelectItem key={option.id} value={option.event_id}>
                {option.event_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedGallery && <GalleryHeader gallery={selectedGallery} />}

      {isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No approved photos yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {items.map((item) => (
            <div
              key={item.id}
              className="relative aspect-square overflow-hidden rounded-lg border bg-muted"
            >
              {galleriesById.get(item.gallery_id)?.cover_media_id === item.id && (
                <Badge className="absolute left-2 top-2 z-10">Cover</Badge>
              )}
              {item.thumbnail_url && (
                <button
                  type="button"
                  aria-label={`View ${item.filename}`}
                  className="block size-full"
                  onClick={() => setLightboxIndex(items.indexOf(item))}
                >
                  <img
                    src={item.thumbnail_url}
                    alt={item.filename}
                    className="size-full object-cover"
                    loading="lazy"
                  />
                </button>
              )}
              <button
                type="button"
                aria-label={`Delete ${item.filename}`}
                disabled={removeMutation.isPending}
                onClick={() => void handleRemove(item)}
                className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black/80 disabled:opacity-50"
              >
                <X className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <Pager
        page={data?.page ?? page}
        perPage={data?.per_page ?? 24}
        total={data?.total ?? 0}
        onPageChange={setPage}
      />

      <GalleryLightbox
        photos={items.map(toLightboxPhoto)}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        canEditTags
        renderPanelExtra={(photoId) => {
          const item = items.find((candidate) => candidate.id === photoId);
          if (!item) return null;
          return (
            <AdminMediaPanel
              item={item}
              pending={false}
              gallery={galleriesById.get(item.gallery_id)}
            />
          );
        }}
      />
      {confirmDialog}
    </div>
  );
}

/**
 * Review queue for member-submitted event photos. Everything shown here lives
 * in a private bucket, so every image is a short-lived signed URL handed out
 * by the server — there are no public links to fall back on.
 */
export function AdminMediaPage() {
  const { data: pending } = trpc.admin.media.pendingCount.useQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "approved" ? "approved" : "pending";

  function handleTabChange(next: string) {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("page");
      if (next === "approved") params.set("tab", "approved");
      else params.delete("tab");
      return params;
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Media</h1>
        <p className="mt-1 text-muted-foreground">
          Review photos members submitted after an event.
        </p>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="pending">Pending{pending ? ` (${pending.count})` : ""}</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
        </TabsList>
        <TabsContent value="pending">
          <PendingQueue />
        </TabsContent>
        <TabsContent value="approved">
          <ApprovedBrowser />
        </TabsContent>
      </Tabs>
    </div>
  );
}
